<template>
    <Page behind-nav class="page-handbook">
        <div class="handbook-header">
            <div class="handbook-header__content container">
                <ProductDropdown id="product-drop" />
                <VersionDropdown />

                <!-- Mobile elements -->
                <p class="handbook-header__product-title" v-html="`${currentProduct.title} ${$t('global.handbook')}`"></p>
                <div class="handbook-header__mobile-controls">
                    <ProButton :aira-label="$t('support.handbook_search_placeholder')" :to="{ name: 'handbook-product', params: { product: 'procreate' } }"
                        ><ProIcon icon="search-thick"
                    /></ProButton>
                    <ProButton
                        @click="showNav = !showNav"
                        :class="{ 'handbook-header__mobile-toggle--active': showNav }"
                        :aria-label="$t('handbook.mobile_button')"
                    >
                        <ProIcon icon="arrow-down" />
                    </ProButton>
                </div>
            </div>
        </div>

        <div class="container handbook-layout">
            <transition-height>
                <div class="handbook-sidebar" v-show="showNav">
                    <div class="handbook-sidebar__content">
                        <div class="handbook-sidebar__navigation">
                            <ProInput
                                :label="$t('a11y-labels.search.keywords')"
                                labelHidden
                                type="text"
                                ref="searchInput"
                                id="search-handbook"
                                v-model="searchTerm"
                                autocomplete="off"
                                :placeholder="$t('global.what_are_you_looking_for')"
                            >
                                <template v-slot:after>
                                    <ProIcon icon="search" />
                                </template>
                            </ProInput>
                            <PageNav v-if="fullNav" :product="currentProduct.slug" :pages="fullNav" :active-pages="activePages" />

                            <div class="handbook-sidebar__version--mobile">
                                <div>
                                    <ProductDropdown id="product-drop-mobile" />
                                </div>
                                <div>
                                    <VersionDropdown id="version-drop-mobile" />
                                </div>
                            </div>

                            <div class="handbook-sidebar__mode-toggle">
                                <LightModeToggle />
                            </div>

                            <span ref="SkipAnchor" id="handbook-skip-anchor" tabindex="-1"></span>
                        </div>
                    </div>
                </div>
            </transition-height>

            <div class="handbook-layout__content" aria-live="polite">
                <div v-if="isSearching" id="search-content">
                    <section class="handbook-search">
                        <div class="handbook-search__header">
                            <div>
                                <ProButton variant="primary" ref="skipToSearch" @click="clearSearch">{{ $t("handbook.searching_clear_search") }}</ProButton>
                            </div>
                            <h2 class="text-heading text-heading--md">{{ $t("handbook.searching_title") }}</h2>
                            <p v-if="searchResults">{{ $tc("handbook.searching_results_count", searchResults.length) }}</p>
                            <p v-else>{{ $t("handbook.searching") }}</p>
                        </div>
                        <ul v-if="searchResults" class="handbook-search__results-container">
                            <li v-for="page in searchResults" :key="page.id" class="handbook-search__results-item">
                                <HandbookSearchResult :page="page" />
                            </li>
                        </ul>
                    </section>
                </div>
                <nuxt-child v-else />
            </div>
        </div>
    </Page>
</template>

<script>
import { mapGetters } from "vuex";
import _, { debounce } from "underscore";
import PageNav from "~/components/handbook/PageNav";
import TransitionHeight from "~/components/TransitionHeight";
import VersionDropdown from "~/components/handbook/VersionDropdown";
import ProductDropdown from "~/components/handbook/ProductDropdown";
import LightModeToggle from "~/components/LightModeToggle";
import HandbookSearchResult from "@/components/handbook/HandbookSearchResult";
import ProInput from "@/components/ProInput";

export default {
    components: { PageNav, TransitionHeight, VersionDropdown, ProductDropdown, LightModeToggle, HandbookSearchResult, ProInput },

    data: () => ({
        showNav: false,
        searchTerm: "",
        preSearchPosition: 0,
    }),

    computed: {
        ...mapGetters({
            page: "handbook/getPage",
            fullNav: "handbook/getPages",
            activePages: "handbook/getActivePages",
            isSearching: "handbook/getIsSearching",
            searchResults: "handbook/getSearchResults",
            currentProduct: "handbook/getCurrentProduct",
            currentVersion: "handbook/getCurrentVersion",
            lightMode: "handbook/getLightMode",
        }),
    },

    async asyncData({ app, store, route, redirect, error }) {
        const product = route.params.product;

        if (!store.getters["handbook/getProduct"](product)) {
            return redirect(
                301,
                app.localePath({
                    name: "handbook-product-all",
                    params: {
                        product: store.getters["handbook/getDefaultProduct"].slug,
                        pathMatch: product + "/" + route.params.pathMatch.slice(0, -1),
                    },
                })
            );
        } else {
            store.commit("handbook/setCurrentProduct", product);
        }

        let version = route.params.pathMatch.split("/")[0];

        if (/^\d+(\.?\d*)*$/g.test(version)) {
            if (!store.getters["handbook/getVersion"](version)) {
                // If version matches semantic versioning format (5.1.x etc) but isn't found, show 404
                return error({ status: 404 });
            }
        } else {
            version = store.getters["handbook/getDefaultVersion"].slug;
        }

        if (version !== store.getters["handbook/getCurrentVersion"]) {
            try {
                await store.dispatch("handbook/fetchPages", {
                    version: version,
                });
            } catch (e) {
                if (e.response?.status === 404) {
                    return error({ statusCode: 404 });
                }

                throw e;
            }
        } else if (!store.state.handbook.pages.length) {
            try {
                await store.dispatch("handbook/fetchPages", {
                    version: version,
                });
            } catch (e) {
                if (e.response?.status === 404) {
                    return error({ statusCode: 404 });
                }

                throw e;
            }
        }
    },

    methods: {
        search: _.debounce(function (e) {
            this.$store.dispatch("handbook/fetchSearchResults", this.searchTerm);
        }, 300),

        clearSearch() {
            this.$store.dispatch("handbook/fetchSearchResults", null);
            this.searchTerm = "";
            if (this.$refs.searchInput) {
                this.$refs.searchInput.focus();
                //incase search term isn't dispalyed at teh time (rotated your device to a smaller viewport)
            }
        },
    },

    watch: {
        searchTerm() {
            this.search();
        },
    },

    created() {
        this.$store.dispatch("handbook/fetchSearchResults", null);
    },

    beforeRouteUpdate(to, from, next) {
        next();
        this.$store.dispatch("handbook/fetchSearchResults", null);
    },

    head() {
        return {
            bodyAttrs: {
                class: this.lightMode ? "light-mode" : null,
            },
        };
    },
};
</script>
